import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import Sheet from '../components/Sheet';
import { PlanListSkeleton } from '../components/Skeletons';
import { PRIMARY, CreditsNote } from '../components/CaptureSheets';
import { useAiCredits } from '../lib/credits';
import { ProBadge } from '../components/ProSheet';
import { nextMonday, toISODate, weekStart } from '../lib/dates';
import { LOCALE, grams, kcal } from '../lib/format';
import { planAverages, planRange, planStatus } from '../lib/plans';
import { useApp, useAppPath } from '../lib/AppContext';
import DateField from '../components/DateField';
import { AnnouncementBanner, NewBadge, useAnnouncementAnchor } from '../components/Announcements';
import usePair from '../cook/usePair';
import InviteSheet from '../cook/InviteSheet';
import PairManager from '../cook/PairManager';
import { ReadyCard, TogetherCard, WaitingCard } from '../cook/PairCards';

const profileLine = (p) => [
  p.name,
  p.weight_kg && `${Number(p.weight_kg).toLocaleString(LOCALE)} kg`,
  p.height_cm && `${p.height_cm} cm`,
  p.training_days_per_week != null && `${p.training_days_per_week} buổi tập/tuần`,
].filter(Boolean).join(' · ');

// Targets and profile come from Settings and go to the AI as they are; the note is only this week's wishes.
// Step "Tuần này ăn cùng ai?" (⑩): alone, or invite someone first (`onInvite`). In a pair, "Cho 2 người"
// makes the menu for both in one AI request.
function CreatePlanForm({ hasCurrentWeek, pair, onInvite, onCreated }) {
  const { api, membership } = useApp();
  const [together, setTogether] = useState(!!pair);
  const cookAnchor = useAnnouncementAnchor('plan_cook_option');
  const credits = useAiCredits(5);
  const path = useAppPath();
  const [profile, setProfile] = useState(null);
  // With no menu for this week yet, start from this Monday; otherwise plan next week.
  const [start, setStart] = useState(() => (hasCurrentWeek ? nextMonday() : weekStart(toISODate(new Date()))));
  const [wishes, setWishes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { api.get('/profile').then(setProfile).catch(() => {}); }, [api]);

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (together && !pair) {
        setBusy(false);
        onInvite();
        return;
      }
      if (credits.blocked()) {
        setBusy(false);
        return;
      }
      const plan = together
        ? (await api.post('/cooking_pair/menu', { week_start: start }, { idempotencyKey: credits.key() })).plan
        : await api.post('/meal_plans', { week_start: start, preferences: wishes }, { idempotencyKey: credits.key() });
      credits.used();
      onCreated(plan);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const missing = profile && !(profile.weight_kg && profile.height_cm && profile.training_days_per_week != null);
  const workout = profile?.workout_protein_goal && profile.workout_protein_goal !== profile.protein_goal;

  return (
    <form onSubmit={create} className="flex flex-col gap-5">
      <div className="flex flex-col gap-2 rounded-[20px] bg-base-200 p-4 text-sm">
        <div>
          <span className="font-semibold">Mục tiêu: </span>
          {profile?.calorie_goal ? `${kcal(profile.calorie_goal)} kcal` : 'chưa đặt calo'}
          {' · '}
          {profile?.protein_goal ? `${grams(profile.protein_goal)} đạm${workout ? ` (ngày tập ${grams(profile.workout_protein_goal)})` : ''}` : 'chưa đặt đạm'}
        </div>
        <div><span className="font-semibold">Hồ sơ: </span>{profile ? profileLine(profile) || 'chưa có' : '…'}</div>
        <Link to={path('/settings')} className="font-semibold text-brand-text underline">
          {missing ? 'Thêm cân nặng, chiều cao và số buổi tập để thực đơn sát hơn' : 'Sửa trong Cài đặt'}
        </Link>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="plan-start" className="text-[15px] font-semibold">Tuần bắt đầu từ</label>
        <DateField id="plan-start" title="Tuần bắt đầu từ" weekday value={start} onChange={setStart} />
      </div>

      <label className="flex flex-col gap-2">
        <span className="text-[15px] font-semibold">Mong muốn tuần này <span className="font-normal text-muted">(không bắt buộc)</span></span>
        <textarea className="textarea min-h-24 w-full rounded-[14px] border-line text-base" value={wishes} disabled={busy}
          placeholder="VD: ăn nhiều cá hơn, nấu sẵn vào Chủ nhật, tiết kiệm" onChange={(e) => setWishes(e.target.value)} />
      </label>

      <fieldset className="flex flex-col gap-2">
        <legend className="pb-2 text-[15px] font-semibold">Tuần này ăn cùng ai?</legend>
        {[[false, 'Chỉ mình tôi', 'Thực đơn theo mục tiêu của bạn.'],
          [true, pair ? `Nấu chung với ${pair.partner.name}` : 'Nấu chung với một người', 'Cùng món ở bữa ăn chung, mỗi người một khẩu phần. Đi chợ một lần.']].map(([value, title, text]) => (
          <button key={title} ref={value ? cookAnchor : null} type="button" aria-pressed={together === value} onClick={() => setTogether(value)}
            className={`flex items-start gap-3 rounded-[16px] border p-3 text-left ${together === value ? 'border-[#17804f] bg-[#eef7f1]' : 'border-line bg-base-100'}`}>
            <span className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border-2 ${together === value ? 'border-[#17804f]' : 'border-line'}`}>
              {together === value && <span className="size-2.5 rounded-full bg-[#17804f]" />}
            </span>
            <span>
              <span className="block font-semibold">{title} {value && <NewBadge anchor="plan_cook_option" />}</span>
              <span className="block text-xs text-muted">{text}</span>
            </span>
          </button>
        ))}
        {together && !pair && <p className="text-xs text-muted">Bước tiếp theo: gửi lời mời. Thực đơn được tạo khi người kia nhận lời, hoặc bạn tạo phần của mình trước nếu không muốn chờ.</p>}
      </fieldset>

      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <div className="flex flex-col items-center gap-2">
        {!(together && !pair) && <CreditsNote text={credits.note} />}
        {membership?.menus?.limit != null && (
          <p className="text-xs text-muted">Tuần này còn {membership.menus.left}/{membership.menus.limit} lần tạo thực đơn</p>
        )}
        <button type="submit" className={PRIMARY} disabled={busy}>
          {busy ? <><span className="loading loading-spinner loading-sm" /> AI đang lên thực đơn…</>
            : together && !pair ? 'Tiếp tục · Mời người nấu chung' : together ? 'Tạo thực đơn cho 2 người' : 'Tạo thực đơn'}
        </button>
        <p className="text-center text-xs text-muted">
          {busy ? 'Có thể mất khoảng một phút, đừng đóng trang.' : 'AI dùng mục tiêu, hồ sơ và nhật ký 14 ngày gần nhất của bạn'}
        </p>
      </div>
    </form>
  );
}

function StatTile({ label, value, valueClass = '', note, noteClass = 'text-muted' }) {
  return (
    <div className="flex flex-col rounded-[14px] bg-base-200 px-4 py-3">
      <span className="text-xs text-muted">{label}</span>
      <span className={`tabular text-xl font-extrabold ${valueClass}`}>{value}</span>
      {note && <span className={`text-xs ${noteClass}`}>{note}</span>}
    </div>
  );
}

function PlanCard({ plan }) {
  const { goals } = useApp();
  const path = useAppPath();
  const status = planStatus(plan);
  const avg = planAverages(plan);
  const firstDay = plan.plan.days?.[0];
  const proteinGoal = goals.protein_g;
  const short = proteinGoal && avg.protein != null ? Math.round(proteinGoal - avg.protein) : null;

  return (
    <article className="flex flex-col gap-4 rounded-[20px] border-t-4 border-[#3ba6ab] bg-base-100 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="tabular text-[22px] font-extrabold leading-tight">{planRange(plan)}</h2>
          <p className="text-sm text-muted">Tạo ngày {new Date(plan.created_at).toLocaleDateString(LOCALE)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${status.chip}`}>{status.label}</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatTile label="Trung bình / ngày" value={`${kcal(avg.calories)} kcal`}
          note={goals.calories ? `mục tiêu ${kcal(goals.calories)}` : null} />
        <StatTile label="Đạm / ngày" value={grams(avg.protein)} valueClass="text-protein"
          note={short == null ? null : short <= 0 ? `Đạt mục tiêu ${grams(proteinGoal)}` : `Thiếu ${short} g so với mục tiêu`}
          noteClass={short != null && short <= 0 ? 'font-semibold text-brand-text' : 'font-semibold text-warning'} />
      </div>

      {firstDay && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{firstDay.day} mở đầu với</div>
          <p className="line-clamp-2 text-[15px]">{firstDay.meals.map((m) => m.name).join(' · ')}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Link to={path(`/plan/${plan.id}`)} className="btn h-11 rounded-[14px] border-2 border-primary bg-base-100 font-bold text-brand-text">
          Xem thực đơn
        </Link>
        <Link to={path(`/plan/${plan.id}?tab=shopping`)} className="btn btn-primary h-11 rounded-[14px] border-0 font-bold">
          Đi chợ cuối tuần
        </Link>
      </div>
    </article>
  );
}

// Weekly menus: this week's and upcoming ones as cards, past weeks under "Trước đây".
export default function PlanPage() {
  const { api, editable, membership, openPaywall } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  // Creating a menu is Pro (the trial included); free accounts get the Pro sheet instead.
  const locked = membership && !membership.pro;
  const startCreating = () => (locked ? openPaywall('pro_required') : setCreating(true));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const createAnchor = useAnnouncementAnchor('plan_create');
  // Nấu chung: the pair (or a pending invite), the invite sheet (② with its intent) and the shared-meal picker.
  const [pairState, setPairState, reloadPair] = usePair();
  const [inviting, setInviting] = useState(null);
  const [slotsOpen, setSlotsOpen] = useState(false);
  const pair = pairState?.pair;
  const invite = pairState?.invite;
  const reloadPlans = () => api.get('/meal_plans').then(setPlans).catch(() => {});

  useEffect(() => {
    api.get('/meal_plans').then(setPlans).catch((e) => setError(e.message));
  }, [api]);

  const active = (plans || []).filter((p) => planStatus(p).kind !== 'past').reverse();
  const past = (plans || []).filter((p) => planStatus(p).kind === 'past');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-[24px] font-extrabold">Thực đơn tuần</h1>
        <span className="flex-1" />
        <PairManager state={pairState} setState={setPairState} onChanged={reloadPlans} slotsOpen={slotsOpen} setSlotsOpen={setSlotsOpen} />
        {editable && (active.length > 0 || !pair) && (
          <button ref={createAnchor} type="button" className="btn btn-primary h-11 rounded-[14px] border-0 px-4 font-bold" onClick={startCreating}>
            <Icon name="plus" className="size-5" /> Tạo mới{locked && <ProBadge />}
          </button>
        )}
      </div>

      <AnnouncementBanner screen="plan" />
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!plans && !error && <PlanListSkeleton />}

      {plans && active.length === 0 && pair && !pair.menus.partner && (
        <ReadyCard pair={pair} weekStart={weekStart(toISODate(new Date()))} onSlots={() => setSlotsOpen(true)}
          onCreated={(plan) => navigate(path(`/plan/${plan.id}`))} />
      )}
      {plans && active.length === 0 && !pair && invite?.intent === 'fresh' && (
        <WaitingCard me={pairState?.me} invite={invite}
          onResend={() => setInviting('fresh')}
          onCancel={async () => { await api.del(`/pair_invites/${invite.id}`); reloadPair(); }}
          onOwnFirst={() => setCreating(true)} />
      )}
      {plans && active.length === 0 && !pair && invite?.intent !== 'fresh' && (
        <div className="flex flex-col items-center gap-3 rounded-[20px] bg-base-100 p-6 text-center">
          <p className="font-semibold">Chưa có thực đơn cho tuần này</p>
          <p className="text-sm text-muted">AI lên thực đơn 7 ngày theo đúng mục tiêu calo và đạm của bạn, kèm danh sách đi chợ.</p>
          {editable && (
            <button type="button" className={PRIMARY} onClick={startCreating}>Tạo thực đơn cho tôi{locked && <ProBadge />}</button>
          )}
          {editable && pairState && (
            <>
              <span className="text-xs text-muted">hoặc</span>
              <div className="w-full text-left"><TogetherCard onInvite={() => setInviting('fresh')} /></div>
            </>
          )}
        </div>
      )}
      {active.map((plan) => <PlanCard key={plan.id} plan={plan} />)}

      {plans && (
        <section className="flex flex-col gap-3 pt-2">
          <h2 className="text-lg font-bold">Trước đây</h2>
          {past.length === 0 ? (
            <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">
              Thực đơn của các tuần đã qua sẽ hiện ở đây.
            </p>
          ) : (
            <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
              {past.map((plan) => {
                const avg = planAverages(plan);
                return (
                  <li key={plan.id}>
                    <Link to={path(`/plan/${plan.id}`)} className="flex min-h-14 items-center justify-between gap-3 py-3">
                      <span>
                        <span className="tabular block font-semibold">{planRange(plan)}</span>
                        <span className="tabular block text-sm text-muted">{kcal(avg.calories)} kcal · {grams(avg.protein)} đạm mỗi ngày</span>
                      </span>
                      <Icon name="chevronRight" className="size-5 text-muted" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      <Sheet open={creating} onClose={() => setCreating(false)} title="Tạo thực đơn tuần">
        <CreatePlanForm
          hasCurrentWeek={(plans || []).some((p) => planStatus(p).kind === 'current')} pair={pair}
          onInvite={() => { setCreating(false); setInviting(active.length === 0 ? 'fresh' : 'existing'); }}
          onCreated={(plan) => { setCreating(false); navigate(path(`/plan/${plan.id}`)); }} />
      </Sheet>
      <InviteSheet open={!!inviting} intent={inviting || 'existing'} onClose={() => { setInviting(null); reloadPair(); }} />
    </div>
  );
}
